import { Resolver, Mutation, Arg, Ctx, Directive } from 'type-graphql'
import { In } from 'typeorm'

import { Theme } from './theme.js'
import { NewTheme, ThemePatch } from './theme-type.js'

@Resolver(Theme)
export class ThemeMutation {
  @Directive('@transaction')
  @Mutation(returns => Theme, { description: 'Creates a new theme.' })
  async createTheme(@Arg('theme') theme: NewTheme, @Ctx() context: ResolverContext): Promise<Theme> {
    const { domain, user, tx } = context.state

    return await tx.getRepository(Theme).save({
      ...theme,
      domain,
      creator: user,
      updater: user
    })
  }

  @Directive('@transaction')
  @Mutation(returns => Theme, { description: 'Updates an existing theme.' })
  async updateTheme(
    @Arg('id') id: string,
    @Arg('patch') patch: ThemePatch,
    @Ctx() context: ResolverContext
  ): Promise<Theme> {
    const { domain, user, tx } = context.state

    const repository = tx.getRepository(Theme)
    const theme = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })

    return await repository.save({
      ...theme,
      ...patch,
      updater: user
    })
  }

  @Directive('@transaction')
  @Mutation(returns => [Theme], {
    description: 'Updates multiple themes at once. Can create or update themes based on the cuFlag.'
  })
  async updateMultipleTheme(
    @Arg('patches', type => [ThemePatch]) patches: ThemePatch[],
    @Ctx() context: ResolverContext
  ): Promise<Theme[]> {
    const { domain, user, tx } = context.state

    let results = []
    const _createRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === '+')
    const _updateRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === 'M')
    const themeRepo = tx.getRepository(Theme)

    if (_createRecords.length > 0) {
      for (let i = 0; i < _createRecords.length; i++) {
        const newRecord = _createRecords[i]

        const result = await themeRepo.save({
          ...newRecord,
          domain,
          creator: user,
          updater: user
        })

        results.push({ ...result, cuFlag: '+' })
      }
    }

    if (_updateRecords.length > 0) {
      for (let i = 0; i < _updateRecords.length; i++) {
        const updateRecord = _updateRecords[i]
        const theme = await themeRepo.findOneBy({ id: updateRecord.id })

        const result = await themeRepo.save({
          ...theme,
          ...updateRecord,
          updater: user
        })

        results.push({ ...result, cuFlag: 'M' })
      }
    }

    return results
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, { description: 'Deletes a single theme by its ID.' })
  async deleteTheme(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state

    await tx.getRepository(Theme).delete({ domain: { id: domain.id }, id })

    return true
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, { description: 'Deletes multiple themes by their IDs.' })
  async deleteThemes(@Arg('ids', type => [String]) ids: string[], @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state

    await tx.getRepository(Theme).delete({
      domain: { id: domain.id },
      id: In(ids)
    })

    return true
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, { description: 'Imports multiple themes.' })
  async importThemes(
    @Arg('themes', type => [ThemePatch]) themes: ThemePatch[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await Promise.all(
      themes.map(async (theme: ThemePatch) => {
        const createdTheme: Theme = await tx.getRepository(Theme).save({ domain, ...theme })
      })
    )

    return true
  }
}
